/**
 * **Le marché des freelances, côté client** ⟦A-4 E · M3.30⟧ — une seule lecture, deux modes.
 *
 * L'offre est une forme close de `(seed, fenêtre, étapes ouvertes, embauches)` — et les deux
 * modes n'ont pas accès aux mêmes ingrédients :
 *
 *  - **bac à sable** : la graine est locale (`run.seed`), on appelle le moteur directement —
 *    exactement ce que fait `resolve` à la publication, donc ce que l'écran montre EST ce que
 *    le moteur débitera ;
 *  - **compte** : la graine ne sort jamais du serveur (SPEC-API §5.4) — c'est
 *    `GET /api/runs/:id/marche` qui rend l'offre, calculée par le même moteur partagé.
 *
 * Les composants ne connaissent que `vueMarche(run)` : la couture est ici, comme
 * `patternsAtelier` pour l'Atelier. C'est le maillon 3 qui manquait depuis M2.10.
 */

import {
  FENETRE_MARCHE_H,
  collectModifiers,
  marche as marcheMoteur,
  marcheOuvert,
  multPrixNotoriete,
  periodeMarche,
  type AtelierStage,
  type GameState,
  type OffreFreelance,
} from "@maxime/shared";

import { contenu } from "../game/content";

import { appelApi } from "./http";

export interface VueMarche {
  ouvert: boolean;
  /** L'offre de la fenêtre, par étape OUVERTE — les étapes fermées n'y figurent pas. */
  etapes: Partial<Record<AtelierStage, readonly OffreFreelance[]>>;
  /** Multiplicateur de tarif au palier courant (⟦A-45 règle 2⟧) — pour l'expliquer. */
  mult_prix: number;
  /** Heures simulées avant que l'offre tourne. */
  fenetre_restante_h: number;
  /** ⟦A-61⟧ La part de chemin que l'arbre fait parcourir aux livraisons, par étape. */
  direction: Record<AtelierStage, number>;
}

/** La vue du marché, quel que soit le mode. */
export async function vueMarche(run: {
  runId: string;
  seed: string;
  bacASable: boolean;
  state: GameState;
}): Promise<VueMarche> {
  if (!run.bacASable) {
    return appelApi<VueMarche>(`/runs/${encodeURIComponent(run.runId)}/marche`);
  }
  const c = contenu();
  const mods = collectModifiers(run.state, c);
  const ouvert = marcheOuvert(run.state, mods);
  return {
    ouvert,
    etapes: ouvert ? marcheMoteur(run.state, c, run.seed, mods) : {},
    mult_prix: multPrixNotoriete(run.state),
    fenetre_restante_h:
      (periodeMarche(run.state.clock_h) + 1) * FENETRE_MARCHE_H - run.state.clock_h,
    direction: mods.freelanceDirection,
  };
}
